import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";

import "../shared/league.css";
import { AnhPhongTo, Avatar, AvatarZoom, initials } from "../shared/Avatar";
import { money } from "../shared/format";
import { Balls, CupVoDich, MatchRow, PlayerName, VO_DICH, You } from "../shared/ui";
import DeBracket from "./Bracket";
import { fetchDeView } from "./api";
import { DeCurrentMatch, DeStage, DeStandingRow, DeVenue, DeView } from "./types";
import BilliardLoader from "../../../components/BilliardLoader";
import FeePaymentModal from "../../../components/league/FeePaymentModal";
import * as SBService from "../../../helpers/sportbookService";
import { pingChallengeDeclined } from "../../../helpers/swissChallengePing";
import { getAssetFullUrl } from "../../../helpers/utils";
import { swissRefreshState, userState } from "../../../states/common";

/**
 * Trang giải Đấu kép của front office.
 *
 * NHẤT QUÁN VỚI TRANG SWISS BẰNG CÁCH DÙNG CHUNG, KHÔNG BẰNG CÁCH CHÉP. Mọi mảnh giao
 * diện lặp lại - thẻ cặp đấu, ô sơ đồ, tên cơ thủ cắt giữa, dãy mạng, nhãn "BẠN", cúp
 * vô địch - nằm ở ../shared/ui.tsx và trang này chỉ gọi. Bố cục (navbar, banner, ba
 * tab, thẻ trạng thái, sheet nhập tỉ số) đi theo đúng khuôn của swiss/live.tsx vì
 * người chơi đi lại giữa hai thể thức trong cùng một app.
 *
 * SỰ KHÁC BIỆT THẬT SỰ CỦA THỂ THỨC NÀY, và chỉ những điều này được phép khác:
 *   1. Không có "vòng" theo nghĩa Swiss. Giải chạy theo SƠ ĐỒ: năm giai đoạn (Vòng 1,
 *      Vòng 2 nhánh thắng, Vòng 3 nhánh thua, vé cuối, rồi vòng chung kết). Cứ đủ hai
 *      người là đánh được, không chờ cả vòng đóng.
 *   2. Hai mạng, và mạng thứ hai mất ở đâu thì quyết định thứ hạng - nên bảng xếp hạng
 *      in theo nhóm đồng hạng dạng khoảng ("5–8") chứ không phải số hạng riêng từng
 *      người. Nhãn khoảng do backend tính; front office không tự dựng lại.
 *   3. Sơ đồ KHÔNG vẽ đường nối. Người thua cũng đi tiếp, sang một cột khác theo luật
 *      đổi chéo, nên tập đường nối không phải cây nhị phân và mấy đoạn .c-out/.c-join
 *      /.c-in của nhánh đơn sẽ vẽ ra một sơ đồ sai. Thay bằng MÃ Ô (T1, W3, L5, CK) -
 *      đúng thứ người trực giải và cơ thủ cùng đọc.
 */

type Tab = "info" | "prize" | "rule" | "reg" | "play" | "rank";

/** Một giai đoạn của sơ đồ trong danh sách trận. Khuôn giống RoundGroup của Swiss. */
const StageGroup = ({
  stage,
  defaultOpen,
  filter,
  myPlayerId,
  openStages,
  toggle,
}: {
  stage: DeStage;
  defaultOpen: boolean;
  filter: "all" | "mine";
  myPlayerId: string | null;
  openStages: Record<string, boolean>;
  toggle: (key: string) => void;
}) => {
  /* Chỉ ô đã đủ HAI người mới vào danh sách trận: ở đây người ta tìm "trận nào đang
     đánh được", và một ô mới có một ghế là ô đang CHỜ cặp phía dưới xong. Vẽ nó ở đây
     là bày ra một trận không ai đánh được - "0/1 trận" cho một ô không có đối thủ. Sơ
     đồ bên tab kia mới là chỗ vẽ đủ cả ô trống, vì ở đó ô trống nói lên đường đi. */
  const all = stage.nodes.filter(
    (node) => node.isBye || node.players.filter((player) => player.id).length === 2,
  );
  const nodes = filter === "mine" ? all.filter((node) => node.mine) : all;
  const key = "s" + stage.stage;
  const open = openStages[key] !== undefined ? openStages[key] : defaultOpen;

  if (nodes.length === 0) {
    return null;
  }
  const done = nodes.filter((node) => node.status === "CONFIRMED" || node.status === "BYE").length;

  return (
    <div className={`roundgrp ${open ? "" : "closed"}`}>
      <div className="rh" onClick={() => toggle(key)}>
        <span className="rt">{stage.name}</span>
        <span className="rc">
          {done}/{nodes.length} trận {open ? "▾" : "▸"}
        </span>
      </div>
      <div className="rb">
        {nodes.map((node) => (
          <MatchRow key={node.code} match={node} myPlayerId={myPlayerId} />
        ))}
      </div>
    </div>
  );
};

export default function DeLeagueLive({ leagueId }: { leagueId: string }) {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();

  const [view, setView] = useState<DeView | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState<Tab>("info");
  const [filter, setFilter] = useState<"all" | "mine">("all");
  const [openStages, setOpenStages] = useState<Record<string, boolean>>({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmSheetOpen, setConfirmSheetOpen] = useState(false);
  const [sheetScore, setSheetScore] = useState<{ me: number; opp: number }>({ me: 0, opp: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [anhPhongTo, setAnhPhongTo] = useState<AnhPhongTo | null>(null);
  const [feeModalOpen, setFeeModalOpen] = useState(false);

  const playerId = user?.id || null;

  // load() được tạo lại theo [leagueId, playerId] nên biến state đọc trong đó có thể
  // cũ; dùng ref để biết chắc màn hình đã có dữ liệu hay chưa.
  const hasView = useRef(false);

  const load = useCallback(async () => {
    try {
      const next = await fetchDeView(leagueId, playerId);
      setView(next);
      hasView.current = true;
      setFailed(false);
    } catch (e: any) {
      // Đã có dữ liệu trên màn hình thì giữ nguyên, chỉ báo lỗi lần nạp này - đừng
      // thay cả trang bằng "Không tìm thấy giải này".
      setFailed(!hasView.current);
      if (e?.response?.data?.error) {
        toast.error("Lỗi, " + e.response.data.error);
      } else {
        const status = e?.response?.status;
        toast.error("Không tải được giải đấu" + (status ? ` (HTTP ${status})` : ""));
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId, playerId]);

  useEffect(() => {
    load();
  }, [load]);

  /* Hộp thoại thách đấu treo ở gốc app vừa làm đổi trạng thái. Nó không dựng lại được
     trang này bằng điều hướng, nên báo bằng một con số tăng dần - cùng một kênh mà
     trang Swiss đang dùng, vì hộp thoại đó là một. */
  const nhipNapLai = useRecoilValue(swissRefreshState);
  useEffect(() => {
    if (nhipNapLai > 0) {
      load();
    }
  }, [nhipNapLai, load]);

  /* Trạng thái trận đổi được mà KHÔNG do người đang xem làm: đối thủ nộp tỉ số, hoặc
     ban tổ chức bốc thăm / sửa kết quả bên back office. Không có đường realtime nào
     cho mấy việc đó, nên tự nạp lại theo nhịp - và CHỈ KHI TAB ĐANG HIỆN, để app chạy
     nền không đốt pin cho một màn hình không ai nhìn. */
  useEffect(() => {
    const dangXem = () => document.visibilityState === "visible";
    const dinhKy = setInterval(() => {
      if (dangXem()) {
        load();
      }
    }, 20000);
    const khiQuayLai = () => {
      if (dangXem()) {
        load();
      }
    };
    document.addEventListener("visibilitychange", khiQuayLai);

    return () => {
      clearInterval(dinhKy);
      document.removeEventListener("visibilitychange", khiQuayLai);
    };
  }, [load]);

  const league = view?.league;
  const banner = getAssetFullUrl(league?.banner);
  const me = view?.me;
  const joined = !!me?.registered;

  /* Giai đoạn mới nhất lên trên: người ta mở trang để xem trận đang đánh, không phải
     để đọc lại Vòng 1. */
  const visibleStages = useMemo(() => (view ? [...view.stages].reverse() : []), [view]);

  /* Chạm đặt riêng cho TỪNG VÒNG, nên một dòng "Chạm 7" cho cả giải là một lời hứa
     sai ở mọi vòng khác. Đọc từ chính sơ đồ đang hiện: mỗi ô mang chạm của vòng nó.
     Mọi vòng bằng nhau thì vẫn in một con số - phần lớn giải là vậy, và "theo vòng"
     ở đó chỉ làm người đọc phải đi tra. */
  const chamText = useMemo(() => {
    const cac = new Set<number>();
    for (const stage of view?.stages ?? []) {
      for (const node of stage.nodes) {
        if (node.race > 0) {
          cac.add(node.race);
        }
      }
    }
    const cuaGiai = view?.league.race ?? 0;
    if (cac.size === 0) {
      return cuaGiai ? `Chạm ${cuaGiai}` : "";
    }

    const danhSach = Array.from(cac);

    return danhSach.length === 1 ? `Chạm ${danhSach[0]}` : "Chạm theo vòng";
  }, [view]);

  const selectedVenue: DeVenue | null = useMemo(() => {
    if (!view || !me?.venueId) {
      return null;
    }

    return view.venues.find((v) => v.id === me.venueId) || null;
  }, [view, me]);

  // Đã đăng ký → mặc định vào tab "Thi đấu" (một lần), sau đó người dùng tự điều hướng.
  const joinedTabInit = useRef(false);
  useEffect(() => {
    if (joined && !joinedTabInit.current) {
      joinedTabInit.current = true;
      setTab("play");
    }
  }, [joined]);

  /* Bộ lọc danh sách trận, đặt MỘT lần khi biết tình trạng người xem. Chưa vào giải
     chính thức thì "Của bạn" luôn rỗng; mở sẵn "Tất cả". */
  const filterInit = useRef(false);
  useEffect(() => {
    if (!view || filterInit.current) {
      return;
    }
    filterInit.current = true;
    setFilter(view.me.isOfficial ? "mine" : "all");
  }, [view]);

  const toggle = (key: string) =>
    setOpenStages((state) => {
      const current = state[key] !== undefined ? state[key] : key === "s" + (visibleStages[0]?.stage ?? 0);

      return { ...state, [key]: !current };
    });

  /* Số cơ thủ còn trong giải: ai chưa bị loại. Bảng xếp hạng đã mang nhãn hạng, mà
     "Đã bị loại" là nhãn nhánh duy nhất nói chắc chắn một người đã về. */
  const alive = useMemo(
    () => (view?.standings ?? []).filter((row) => row.branch !== "Đã bị loại").length,
    [view],
  );

  const handleJoin = async () => {
    if (!league) {
      return;
    }
    if (!playerId) {
      toast.error("Vui lòng đăng nhập để đăng ký giải");

      return navigate("/login");
    }
    setBusy(true);
    try {
      await SBService.joinDeLeague(league.id);
      toast.success(league.hasFee ? "Đăng ký thành công, chờ xác nhận đóng phí" : "Đăng ký thành công");
      setModalOpen(false);
      setTab("play");
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      /* Dịch đúng những mã mà /de/join thật sự ném ra. bracketAlreadyDrawn và
         leagueHasChampion là hai mã RIÊNG của thể thức này: sơ đồ đã bốc thì thêm một
         người là thêm một người không có ô. */
      const messages: Record<string, string> = {
        alreadyJoinedLeague: "Bạn đã đăng ký giải này",
        bracketAlreadyDrawn: "Giải đã bốc sơ đồ, không nhận thêm đăng ký",
        leagueHasChampion: "Giải đã có vô địch",
        leagueFull: "Giải đã đủ số cơ thủ",
        registrationClosed: "Đã hết hạn đăng ký",
        venueRequired: "Vui lòng chọn địa điểm thi đấu",
        venueFull: "Địa điểm đã hết suất",
        venueNotInLeague: "Địa điểm không thuộc giải này",
        playerNotCalibrated: "Bạn cần được xếp hạng trước khi đăng ký",
        playerRankIsNotAllowed: "Hạng của bạn không thuộc nhóm được tham gia",
      };
      toast.error(messages[code] || "Không đăng ký được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  /** Tỉ số đang chờ xác nhận, quy về góc nhìn của tôi. */
  const pendingScore = (current: DeCurrentMatch | null | undefined) => {
    const pending = current?.pendingResult;
    if (!pending || !playerId) {
      return null;
    }
    const iAmChallenger = current?.challengerId === playerId;

    return {
      me: Number((iAmChallenger ? pending.challengerPoint : pending.acceptorPoint) ?? 0),
      opp: Number((iAmChallenger ? pending.acceptorPoint : pending.challengerPoint) ?? 0),
    };
  };

  const openResultSheet = () => {
    const current = me?.match;
    if (!current || !league) {
      return;
    }
    /* Đã có tỉ số chờ xác nhận thì mở sheet với đúng tỉ số đó - dù là của mình (sửa
       lại) hay của đối thủ (nhập tỉ số khác). Chưa có gì thì bắt đầu từ 0 để không
       submit nhầm điểm mặc định. */
    setSheetScore(pendingScore(current) ?? { me: 0, opp: 0 });
    setSheetOpen(true);
  };

  const handleSubmitResult = async () => {
    const current = me?.match;
    if (!current?.matchId || !league || !playerId) {
      return;
    }
    /* Quy tỉ số theo chiều của TRẬN. challengerId của trận có thể ngược chiều cặp đấu
       (ai quét mã trước thì người đó là challenger), nên lấy theo ghế của cặp là gửi
       ngược - người thắng vào rating thành người thua, và không có lỗi nào được ném. */
    const iAmChallenger = current.challengerId === playerId;
    setBusy(true);
    try {
      await SBService.submitMatch(current.matchId, {
        challengerPoint: iAmChallenger ? sheetScore.me : sheetScore.opp,
        acceptorPoint: iAmChallenger ? sheetScore.opp : sheetScore.me,
        challengerId: current.challengerId,
        acceptorId: current.acceptorId,
        billPhoto: null,
        billAmount: 0,
      });
      toast.success("Đã gửi kết quả, chờ đối thủ xác nhận");
      setSheetOpen(false);
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === "pointNotMatchedToRace") {
        toast.error("Tỉ số chưa đủ chạm " + (current.race || league.race));
      } else {
        toast.error("Không gửi được kết quả");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmResult = async () => {
    const current = me?.match;
    if (!current?.matchId || !current.pendingResult) {
      return;
    }
    setBusy(true);
    try {
      await SBService.confirmResult(
        current.matchId,
        current.pendingResult.id,
        current.challengerId || "",
        current.acceptorId || "",
      );
      toast.success("Đã xác nhận kết quả");
      setConfirmSheetOpen(false);
      await load();
    } catch (e: any) {
      /* Nuốt mã lỗi khiến mọi nguyên nhân trông giống nhau và không debug được. Mã nào
         biết thì dịch, mã lạ thì hiện nguyên văn để còn lần ra. */
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        matchNotFound: "Không tìm thấy trận hoặc kết quả cần xác nhận",
        matchNotAcceptedOrSubmitted: "Trận chưa ở trạng thái chờ xác nhận",
        acceptorIdNotMatched: "Dữ liệu trận không khớp, vui lòng tải lại trang",
        challengerIdNotMatched: "Dữ liệu trận không khớp, vui lòng tải lại trang",
        notPlayerInMatch: "Bạn không thuộc trận này",
        playersNotFoundInLeague: "Thiếu dữ liệu đăng ký của cơ thủ trong giải",
        playersIsNotValid: "Cơ thủ chưa có hạng nên chưa tính được điểm",
      };
      toast.error(messages[code] || (code ? `Không xác nhận được kết quả (${code})` : "Không xác nhận được kết quả"));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Nhận lời thách đấu của đối thủ.
   *
   * Không có endpoint riêng: bên nào cũng gọi cùng một API thách đấu, backend coi ai
   * gọi sau là người đồng ý. Nhờ vậy "bấm xác nhận" ở đây và "quét lại mã của họ" là
   * hai đường dẫn tới cùng một kết quả.
   */
  const handleAcceptChallenge = async () => {
    const opponentId = me?.match?.opponentId;
    if (!league || !opponentId) {
      return;
    }
    setBusy(true);
    try {
      await SBService.deChallenge(league.id, opponentId);
      toast.success("Đã xác nhận, mời nhập kết quả sau khi đánh xong");
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        notPairedInBracket: "Người này không phải đối thủ của bạn trong sơ đồ",
        matchAlreadyFinished: "Trận này đã có kết quả",
        leagueHasChampion: "Giải đã có vô địch",
        feeNotPaid: "Cần được xác nhận đóng phí trước khi thi đấu",
      };
      toast.error(messages[code] || "Không xác nhận được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Từ chối lời mời: trận trở lại "chưa ai quét", mời lại được từ đầu.
   *
   * Báo ngược cho người mời qua Firebase để họ khỏi ngồi chờ một lời mời đã bị từ
   * chối. Ghi hỏng thì thôi - họ vẫn thấy đúng trạng thái khi mở lại trang.
   */
  const handleDeclineChallenge = async () => {
    const opponentId = me?.match?.opponentId;
    if (!league || !opponentId) {
      return;
    }
    setBusy(true);
    try {
      await SBService.deDeclineChallenge(league.id, opponentId);
      await pingChallengeDeclined(opponentId, league.id, user?.name || "");
      toast.success("Đã từ chối lời thách đấu");
      await load();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      const messages: Record<string, string> = {
        noPendingChallenge: "Lời mời này không còn nữa",
        onlyInvitedCanDecline: "Đây là lời mời của chính bạn",
        matchNotFound: "Trận không còn nữa, vui lòng tải lại trang",
      };
      toast.error(messages[code] || "Không từ chối được, vui lòng thử lại");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="biadi">
        <BilliardLoader variant="center" label="Đang tải giải đấu…" />
      </div>
    );
  }

  if (failed || !view || !league || !me) {
    return (
      <div className="biadi">
        <div className="shell">
          <div className="pad">
            <div className="emptybox">Không tìm thấy giải Đấu kép này.</div>
          </div>
        </div>
      </div>
    );
  }

  /* Giai đoạn của giải suy theo mốc thời gian cấu hình, rồi mới tới cờ trạng thái:
     bốc sơ đồ là thao tác thủ công của ban tổ chức nên có thể xảy ra trước ngày cấu
     hình, và API /de/join khoá đăng ký theo CỜ đó (bracketAlreadyDrawn) chứ không
     theo ngày. */
  const nowM = moment();
  const hasTimeline = !!league.startDate;
  const startedByDate = !!league.startDate && nowM.isSameOrAfter(moment(league.startDate));
  const drawn = view.drawn;
  const started = hasTimeline ? startedByDate : drawn;
  const finished = league.isFinished || (!!league.endDate && nowM.isAfter(moment(league.endDate).endOf("day")));
  const beforeReg = !!league.startRegDate && nowM.isBefore(moment(league.startRegDate));
  const regClosed = !!league.endRegDate && nowM.isAfter(moment(league.endRegDate).endOf("day"));
  /* Đăng ký chỉ khoá theo KHUNG ĐĂNG KÝ và việc đã bốc sơ đồ thật, KHÔNG theo ngày bắt
     đầu giải: giải một ngày (đăng ký, khai mạc, thi đấu cùng một ngày) phải đăng ký được
     trong ngày thi đấu miễn khung đăng ký còn mở. API /de/join cũng khoá theo cờ bốc. */
  const canRegister = !finished && !beforeReg && !regClosed && !drawn;
  const regCta = finished
    ? "Giải đã kết thúc"
    : drawn
      ? "Giải đã bốc sơ đồ"
      : beforeReg
        ? "Chưa mở đăng ký"
        : regClosed
          ? "Đã hết hạn đăng ký"
          : "Tham gia ngay";

  /* Đã đăng ký, chờ đóng phí, nhưng giải đã bắt đầu và không còn nhận đăng ký nữa
     (hết hạn đăng ký / đã bốc sơ đồ / đã kết thúc) → đăng ký không thành công. */
  const feeFailed = me.state === "waiting_fee" && (finished || drawn || (started && regClosed));

  /** Nhà vô địch, nếu đã có. Bảng xếp hạng chốt điều đó bằng ghi chú, không phải hạng. */
  const champion = view.standings.find((row) => row.note === VO_DICH) ?? null;

  /* ------------------------------ Khối dùng lại ------------------------------ */

  const Navbar = ({ brand = true }: { brand?: boolean }) => (
    <div className="navbar">
      <button className="back" onClick={() => navigate(-1)}>
        ‹
      </button>
      <span className="ttl">Thông tin giải đấu</span>
      {brand && (
        <span className="brandmark">
          <span className="bmk">B</span>BIADI
        </span>
      )}
    </div>
  );

  const renderTabPrize = () => (
    <div className="sec">
      {view.prizes.length === 0 ? (
        <div className="tiny">Ban tổ chức chưa công bố cơ cấu giải thưởng.</div>
      ) : (
        <>
          <div className="prizehead">
            <span>Thứ hạng</span>
            <span style={{ textAlign: "right" }}>Tiền mặt</span>
            <span>Hiện vật</span>
          </div>
          {view.prizes.map((prize, index) => (
            <div key={index} className="prizerow">
              <span className="p1">{prize.label}</span>
              <span className="p2">{prize.cash ? `${money(prize.cash)} đ` : "—"}</span>
              <span className="small">{prize.kind || "—"}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );

  const renderTabRule = () => (
    <div className="sec rulebody">
      <div className="metarow" style={{ marginBottom: 10 }}>
        {/* Nhãn thể thức do backend trả ("Đấu kép"); không gõ lại ở front office. */}
        <span className="chip solid">{league.format}</span>
        <span className="dot" />
        <span>2 mạng</span>
        <span className="dot" />
        <span>{chamText}</span>
      </div>
      <div className="tiny" style={{ marginBottom: 10 }}>
        Thua một trận là xuống nhánh thua, thua trận thứ hai là dừng bước. Vào vòng chung kết bằng hai
        đường: thắng cả hai trận đầu, hoặc thắng ở nhánh thua để lấy vé cuối.
      </div>
      {/* Thể lệ & luật thi đấu lấy từ back office (không gõ cứng). */}
      {league.rule ? (
        <div className="raw-css" dangerouslySetInnerHTML={{ __html: league.rule }} />
      ) : (
        <div className="tiny">Ban tổ chức chưa công bố thể lệ chi tiết.</div>
      )}
      {league.fee ? (
        <div className="tiny" style={{ marginTop: 10 }}>
          Lệ phí tham gia <b>{money(league.fee)} đ</b>
          {league.feeQrImage || league.feeTransferNote
            ? ", chuyển khoản theo mã QR ở ô Đóng phí."
            : ", đóng tại địa điểm khi check-in."}
        </div>
      ) : null}
    </div>
  );

  const renderTabRegistered = () => (
    <div className="sec" style={{ padding: "18px 12px", textAlign: "center" }}>
      <div className="tiny">
        {league.registered > 0
          ? `${league.registered}/${league.players || league.registered} cơ thủ đã đăng ký` +
            (league.pendingFee > 0 ? ` • ${league.pendingFee} đang chờ xác nhận đóng phí` : "")
          : "Chưa có cơ thủ nào đăng ký"}
      </div>
    </div>
  );

  const renderStatusAction = () => {
    const current = me.match;
    const oppName = current?.opponentName || "Đối thủ";

    const opponentBlock = current?.opponentId ? (
      <div className="opp opp-ten2">
        <Avatar
          src={me.avatar}
          name={user?.name || ""}
          me
          rankClass={me.rankClass}
          score={me.rating}
          onZoom={setAnhPhongTo}
        />
        {/* CHỈ TÊN, cả hai bên. Dòng phụ cũ phá nghĩa của thẻ: bên trái là "Còn N
            mạng" (về BẠN), bên phải là chạm (về TRẬN) - đặt đối xứng hai bên tên hai
            người thì mắt đọc dòng bên phải như một thuộc tính của đối thủ. Bỏ một
            bên thì thẻ lệch, nên bỏ cả hai.
            Số mạng không mất đi: ô "Mạng còn" ngay phía trên thẻ đã in nó bằng hai
            quả bóng. Chạm vẫn còn ở dòng tóm tắt dưới thẻ và ở màn nhập tỉ số. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="nm">Bạn</div>
        </div>
        <div className="vsbadge">VS</div>
        <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
          <div className="nm">{oppName}</div>
        </div>
        <Avatar
          src={current?.opponentAvatar}
          name={oppName}
          rankClass={current?.opponentRankClass}
          score={current?.opponentRating}
          onZoom={setAnhPhongTo}
        />
      </div>
    ) : null;

    switch (me.state) {
      case "waiting_start":
        return (
          <>
            <div className="kicker">Chờ ban tổ chức bốc sơ đồ</div>
            <div className="expl">
              Bạn đã có suất
              {selectedVenue ? (
                <>
                  {" "}
                  tại <b style={{ color: "var(--chalk)" }}>{selectedVenue.name}</b>
                </>
              ) : null}
              . Sơ đồ nhánh kép được bốc một lần cho cả giải — bốc xong là thấy ngay đối thủ Vòng 1 của bạn.
            </div>
            <div className="btns">
              <button className="btn btn-quiet" onClick={() => setTab("info")}>
                Xem thể thức
              </button>
            </div>
          </>
        );
      case "has_match": {
        const started = !!current?.started;
        const challenge = current?.challenge ?? null;

        /* Đã có lời mời nhưng trận chưa bắt đầu: một bên đã quét mã, đang chờ bên kia
           gật đầu. Người được mời xác nhận bằng chính lời thách đấu ngược lại -
           backend coi ai gọi sau là người đồng ý. */
        if (!started && challenge) {
          return (
            <>
              <div className="kicker">
                {challenge.byMe ? "Đang chờ đối thủ xác nhận" : `${oppName} muốn bắt đầu trận`}
              </div>
              {opponentBlock}
              <div className="expl">
                {challenge.byMe
                  ? "Bạn đã quét mã của đối thủ. Trận chỉ bắt đầu, và mở nhập kết quả, sau khi họ xác nhận."
                  : "Họ đã quét mã QR của bạn. Xác nhận để bắt đầu trận; sau đó cả hai mới nhập được tỉ số."}
              </div>
              <div className="btns">
                {challenge.byMe ? (
                  <button className="btn btn-ghost" onClick={() => navigate("/my-qr")}>
                    QR của tôi
                  </button>
                ) : (
                  <>
                    <button className="btn btn-primary" disabled={busy} onClick={handleAcceptChallenge}>
                      Xác nhận thi đấu
                    </button>
                    <button className="btn btn-ghost" disabled={busy} onClick={handleDeclineChallenge}>
                      Từ chối
                    </button>
                  </>
                )}
              </div>
            </>
          );
        }

        return (
          <>
            {/* Không ghép tên vòng vào đây: `branch` là VỊ TRÍ của tôi trong sơ đồ
                ("Nhánh thua", "Vòng chung kết"), không phải tên vòng của trận - ghép vào
                thành "trận ở nhánh thua", đọc ra là một cái tên vòng không có. */}
            <div className="kicker">Bạn có trận</div>
            {opponentBlock}
            {started ? (
              <div className="btns">
                <button className="btn btn-primary" onClick={openResultSheet}>
                  Nhập kết quả
                </button>
              </div>
            ) : (
              <>
                <div className="expl">
                  Gặp đối thủ tại bàn rồi đưa mã QR của bạn cho họ quét — hoặc quét mã của họ — để bắt đầu
                  trận.
                </div>
                <div className="btns">
                  <button className="btn btn-primary" onClick={() => navigate("/my-qr")}>
                    QR của tôi
                  </button>
                  <button className="btn btn-ghost" onClick={() => navigate(`/scan-qr/${league.id}`)}>
                    Quét QR đối thủ
                  </button>
                </div>
              </>
            )}
          </>
        );
      }
      case "waiting_confirm": {
        const pending = current?.pendingResult;
        const mineSubmitted = !!pending?.submittedByMe;
        const score = pendingScore(current);

        return (
          <>
            <div className="kicker">{mineSubmitted ? "Chờ đối thủ xác nhận" : "Đối thủ đã nhập kết quả"}</div>
            {score ? (
              <div className="pendingscore">
                <span className="ps-side">Bạn</span>
                <span className="ps-val">
                  {score.me}
                  <span className="ps-sep">–</span>
                  {score.opp}
                </span>
                <span className="ps-side ps-right">{oppName}</span>
              </div>
            ) : null}
            <div className="expl">
              {mineSubmitted
                ? "Kết quả chỉ được tính sau khi đối thủ xác nhận."
                : "Kiểm tra tỉ số đối thủ đã nhập rồi xác nhận. Sau khi xác nhận, trận được tính vào rating Biadi và không sửa được."}
            </div>
            {opponentBlock}
            <div className="btns">
              {mineSubmitted ? (
                <button className="btn btn-ghost" onClick={openResultSheet} disabled={busy}>
                  Sửa kết quả
                </button>
              ) : (
                <>
                  <button className="btn btn-primary" onClick={() => setConfirmSheetOpen(true)} disabled={busy}>
                    Xác nhận kết quả
                  </button>
                  <button className="btn btn-ghost" onClick={openResultSheet} disabled={busy}>
                    Nhập tỉ số khác
                  </button>
                </>
              )}
            </div>
          </>
        );
      }
      case "waiting_pairing":
        return (
          <>
            <div className="kicker">
              {me.duocMienDau ? "Bạn được miễn đấu — chờ biết đối thủ" : "Bạn đi tiếp — chờ biết đối thủ"}
            </div>
            <div className="expl">
              {me.duocMienDau ? (
                <>
                  Số người không tròn khung nên bạn vào thẳng vòng sau, <b style={{ color: "var(--chalk)" }}>không
                  mất mạng nào</b>.{" "}
                </>
              ) : null}
              Ô kế tiếp của bạn trong sơ đồ chưa đủ hai người: cặp sinh ra đối thủ của bạn chưa đấu xong.
              {league.koMode === "draw" ? (
                <> Nếu bốn vòng loại đã xong thì ban tổ chức còn phải bốc thăm vòng chung kết.</>
              ) : null}{" "}
              Sơ đồ cập nhật ngay khi cặp đó có kết quả.
            </div>
            {/* KHÔNG có nút QR ở đây: chưa biết đối thủ thì không có mã nào để quét,
                và bày nút ra là mời người ta ra bàn tìm một người chưa tồn tại. */}
            <div className="btns">
              <button className="btn btn-primary" onClick={() => setTab("rank")}>
                Xem BXH
              </button>
              <button className="btn btn-quiet" onClick={() => setTab("info")}>
                Thông tin chung
              </button>
            </div>
          </>
        );
      case "eliminated": {
        const mine = view.standings.find((row) => row.playerId === playerId) ?? null;

        return (
          <>
            <div className="kicker" style={{ color: "var(--steel)" }}>
              Bạn đã dừng bước
            </div>
            <div className="expl">
              {mine?.note ? `${mine.note}. ` : ""}
              Bạn thua đủ hai trận nên hết mạng
              {mine?.rankLabel && mine.rankLabel !== "—" ? (
                <>
                  {" "}
                  và kết thúc giải ở <b style={{ color: "var(--chalk)" }}>hạng {mine.rankLabel}</b>
                </>
              ) : null}
              .
            </div>
            <div className="btns">
              <button className="btn btn-ghost" onClick={() => setTab("rank")}>
                Xem thứ hạng
              </button>
              <button
                className="btn btn-quiet"
                onClick={() => {
                  setFilter("mine");
                  setTab("play");
                }}
              >
                Xem lại trận của tôi
              </button>
            </div>
          </>
        );
      }
      case "finished": {
        const mine = view.standings.find((row) => row.playerId === playerId) ?? null;
        const toiVoDich = mine?.note === VO_DICH;

        return (
          <>
            <div className="kicker">{toiVoDich ? "Bạn vô địch giải này" : "Giải đã kết thúc"}</div>
            <div className="expl">
              Bạn xếp{" "}
              <b style={{ color: "var(--red)", fontFamily: "var(--ff-d)", fontSize: 17 }}>
                hạng {mine?.rankLabel ?? "—"}
              </b>
              /{league.official || view.standings.length}
              {mine?.note ? ` • ${mine.note}` : ""}.
              {champion && !toiVoDich ? (
                <>
                  {" "}
                  Vô địch: <b style={{ color: "var(--chalk)" }}>{champion.name}</b>.
                </>
              ) : null}
            </div>
            <div className="btns">
              <button className="btn btn-primary" onClick={() => setTab("rank")}>
                Xem thứ hạng chung cuộc
              </button>
            </div>
          </>
        );
      }
      case "waiting_fee":
        // Giải đã bắt đầu mà BTC chưa xác nhận đóng phí → đăng ký không thành công.
        if (feeFailed) {
          return (
            <>
              <div className="kicker" style={{ color: "var(--st-dead)" }}>
                Đăng ký không thành công
              </div>
              <div className="expl">
                Giải đã bốc sơ đồ nhưng BTC chưa xác nhận bạn đóng phí, nên bạn không có ô nào trong sơ đồ.
                Vui lòng liên hệ ban tổ chức để được hỗ trợ.
              </div>
              <div className="btns">
                <button className="btn btn-quiet" onClick={() => setTab("info")}>
                  Xem thông tin giải
                </button>
              </div>
            </>
          );
        }

        return (
          <>
            <div className="kicker" style={{ color: "var(--st-wait)" }}>
              Chờ ban tổ chức xác nhận đóng phí
            </div>
            <div className="expl">
              {league.fee ? (
                <>
                  Phí tham gia <b style={{ color: "var(--chalk)" }}>{money(league.fee)} đ</b>
                  {league.feeQrImage || league.feeTransferNote ? (
                    <>. Bấm Đóng phí để lấy mã QR và nội dung chuyển khoản. </>
                  ) : (
                    <> — đóng tại địa điểm khi check-in. </>
                  )}
                </>
              ) : null}
              Bạn <b style={{ color: "var(--chalk)" }}>chưa được đưa vào sơ đồ</b> cho tới khi BTC xác nhận đã
              đóng phí.
            </div>
            <div className="btns">
              {/* Giải cấu hình chuyển khoản mới có gì để mở; giải thu tiền mặt thì ô
                  này rỗng nên không bày nút ra. */}
              {league.feeQrImage || league.feeTransferNote ? (
                /* Nút CHÍNH, không phải nút trơn: ở trạng thái này Đóng phí là việc
                   duy nhất đưa người chơi đi tiếp - chưa đóng thì không được đưa vào
                   sơ đồ. Và "btn" không kèm biến thể nào thì nền trong suốt, không
                   viền, nên nó gần như vô hình bên cạnh nút xám ngay cạnh. */
                <button className="btn btn-primary" onClick={() => setFeeModalOpen(true)}>
                  Đóng phí
                </button>
              ) : null}
              <button className="btn btn-quiet" onClick={() => setTab("info")}>
                Xem thông tin giải
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderStatusCard = () => {
    const chipMap: Record<string, [string, string]> = {
      waiting_fee: ["chip wait", "Chờ đóng phí"],
      waiting_start: ["chip", "Chờ bốc sơ đồ"],
      has_match: ["chip live", "Có trận"],
      waiting_confirm: ["chip wait", "Chờ xác nhận"],
      waiting_pairing: ["chip wait", "Chờ đối thủ"],
      eliminated: ["chip dead", "Bị loại"],
      finished: ["chip solid", "Kết thúc"],
    };
    const [chipClass, chipText] = feeFailed
      ? ["chip dead", "Đăng ký thất bại"]
      : chipMap[me.state] || ["chip", "Đang diễn ra"];
    const mine = view.standings.find((row) => row.playerId === playerId) ?? null;

    return (
      <div className="status">
        <div className="sh">
          <span className="rnd">{drawn ? (me.branch ?? "Sơ đồ đã bốc") : "Trước khi bốc sơ đồ"}</span>
          <span className={chipClass}>{chipText}</span>
        </div>
        <div className="stats4">
          {/* Vào vòng chung kết rồi thì SỐ MẠNG không còn nghĩa gì: ai cũng còn đúng
              một mạng, thua là về. Giữ ô đó là chiếm một trong hai chỗ quý nhất của
              thẻ bằng một con số không bao giờ đổi. Thay bằng tên vòng đang đánh -
              "Tứ kết" nói đúng thứ cơ thủ muốn biết lúc này. */}
          <div className="st">
            <div className="k">{me.vongChungKet ? "Vòng" : "Mạng còn"}</div>
            {me.vongChungKet ? (
              /* `sm` chứ không phải cỡ chữ mặc định của ô: 19px/800 là cỡ của một
                 CON SỐ ("Hạng 5", hai quả bóng mạng). Tên vòng là một cụm CHỮ, ở cỡ
                 ấy nó to bất thường và "Vòng 1/8" tràn xuống hai dòng. */
              <div className="v sm">{me.match?.roundLabel ?? "Vòng chung kết"}</div>
            ) : (
              /* Hai mạng là ĐỊNH NGHĨA của thể thức, không phải tham số cấu hình. */
              <Balls lives={me.lives ?? 0} total={2} />
            )}
          </div>
          <div className="st">
            <div className="k">Hạng</div>
            {/* Nhãn hạng có thể là một KHOẢNG ("5–8"): bốn người thua tứ kết đồng hạng
                thật, và in một số riêng cho mỗi người là bày ra một thứ tự không có. */}
            <div className="v">{mine?.rankLabel ?? "—"}</div>
          </div>
        </div>
        <div className="action">
          {renderStatusAction()}
        </div>
        <div className="rankline">
          <span className="rl">
            {league.isFinished
              ? "Giải đấu đã kết thúc"
              : drawn
                ? `Còn ${alive}/${league.official} cơ thủ • ${league.format}, ${chamText.toLowerCase()}`
                : `${league.registered} cơ thủ đã đăng ký • ${league.format}, ${chamText.toLowerCase()}`}
          </span>
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              setTab("rank");
            }}
          >
            Sơ đồ ›
          </a>
        </div>
      </div>
    );
  };

  const renderTabPlay = () => {
    const groups = visibleStages
      .map((stage, index) => (
        <StageGroup
          key={stage.stage}
          stage={stage}
          defaultOpen={index === 0}
          filter={filter}
          myPlayerId={playerId}
          openStages={openStages}
          toggle={toggle}
        />
      ))
      .filter(Boolean);

    return (
      <div className="playgrid">
        <div className="col-left">
          {renderStatusCard()}
        </div>
        <div className="col-right">
          <div className="mfilter">
            <div className="seg">
              <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>
                Tất cả
              </button>
              <button className={filter === "mine" ? "on" : ""} onClick={() => setFilter("mine")}>
                Của bạn
              </button>
            </div>
          </div>
          {!drawn ? (
            <div className="emptybox">
              Chưa bốc sơ đồ. Danh sách trận hiện ngay khi ban tổ chức bốc thăm nhánh kép.
            </div>
          ) : groups.every((group) => group === null) ? (
            <div className="emptybox">Bạn chưa có trận nào ở bộ lọc này.</div>
          ) : (
            groups
          )}
        </div>
      </div>
    );
  };

  const renderTabRank = () => {
    const rows = view.standings;
    const mine = rows.find((row) => row.playerId === playerId) ?? null;
    const laVoDich = (row: DeStandingRow) => row.note === VO_DICH;

    const myBar = mine ? (
      <div className="myrankbar">
        <span className="rkcell" style={{ color: "var(--red)" }}>
          #{mine.rankLabel}
        </span>
        <Avatar
          src={mine.avatar}
          name={mine.name}
          me
          rankClass={mine.rankClass}
          score={mine.rating}
          style={{ width: 28, height: 28, fontSize: 11 }}
          onZoom={setAnhPhongTo}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* flex chứ không để chữ trần: cúp là anh em của tên, không có flex thì nó
              dính sát vào chữ cuối cùng. */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13.5 }}>
            {mine.name}
            {laVoDich(mine) ? <CupVoDich /> : null}
          </div>
          <div className="tiny">
            {mine.note || mine.branch || "—"}
            {mine.branch !== "Đã bị loại" ? ` • ${mine.lives} mạng` : ""}
          </div>
        </div>
        <span className="chip red">Bạn</span>
      </div>
    ) : null;

    const table = (
      <div className="sec" style={{ padding: "6px 10px 10px" }}>
        <table className="tbl">
          <thead>
            <tr>
              {/* 56 chứ không 44 như Swiss: ô này in được một KHOẢNG ("5–8"), dài hơn
                  một con số, và bảng dùng table-layout fixed nên thiếu chỗ là đè chữ. */}
              <th style={{ width: 56 }}>Hạng</th>
              <th className="l">Cơ thủ</th>
              {/* BA cột, không bốn: thêm cột "Mạng" thì ở 390px cột tên chỉ còn ~100px
                  và PlayerName cắt giữa ăn mất gần hết tên ("Nguyễ…ăn An"). Mạng còn
                  đã nằm ở thẻ trạng thái và ở dòng của tôi ngay trên bảng, còn ghi chú
                  ("Dừng ở Vòng 3") mới là thứ giải thích thứ hạng. Cùng lựa chọn mà
                  tab nhánh của Swiss đã phải làm, vì cùng một chỗ chật. */}
              <th style={{ width: 116 }}>Kết quả</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const dead = row.branch === "Đã bị loại";
              const isMe = row.playerId === playerId;

              return (
                <tr key={row.playerId || index} className={`${isMe ? "me" : ""} ${dead ? "dead" : ""}`}>
                  <td>
                    <span className="rkcell">{row.rankLabel}</span>
                  </td>
                  <td className="l">
                    <div className="namecell">
                      <Avatar
                        src={row.avatar}
                        name={row.name}
                        size="avas"
                        me={isMe}
                        rankClass={row.rankClass}
                        score={row.rating}
                        onZoom={setAnhPhongTo}
                      />
                      <PlayerName name={row.name} />
                      {laVoDich(row) ? <CupVoDich /> : null}
                      {isMe && <You />}
                    </div>
                  </td>
                  <td>
                    {/* hai-dong: ghi chú của nhánh kép dài hơn của Swiss, một dòng ở
                        390px cắt mất đúng phần phân biệt. Xem league.css. */}
                    <span className="tiny ghi-chu-nhanh hai-dong" title={row.note || row.branch || "—"}>
                      {row.note || row.branch || "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

    if (!drawn) {
      return (
        <>
          <div className="sec-h">
            <span className="label">Danh sách cơ thủ</span>
          </div>
          <div className="snapnote">
            Chưa bốc sơ đồ nên chưa có thứ hạng. Thứ tự dưới đây là thứ tự đăng ký.
          </div>
          {myBar}
          {table}
        </>
      );
    }

    return (
      <>
        <div className="sec-h">
          <span className="label">Sơ đồ nhánh kép</span>
        </div>
        <div className="snapnote">
          Mỗi cơ thủ có hai mạng: thua một trận là xuống nhánh thua, thua trận thứ hai là dừng bước. Mã ô
          (T1, W3, L5, CK) là mã ban tổ chức dùng, nên đọc đúng ô nào là ô nào. Đồng hạng là chuyện bình thường — bốn người thua cùng một vòng thì cùng hạng.
        </div>
        <DeBracket stages={view.stages} myPlayerId={playerId} />
        <div className="sec-h">
          <span className="label">{league.isFinished ? "Thứ hạng chung cuộc" : "Thứ hạng tạm tính"}</span>
        </div>
        {myBar}
        {table}
        <div className="tiny" style={{ padding: "10px 2px" }}>
          Tiêu chí xếp: đi sâu hơn trong sơ đồ đứng trên <span className="dot mut" /> cùng vòng bị loại thì
          đồng hạng <span className="dot mut" /> rating Biadi ↓
        </div>
      </>
    );
  };

  const renderTabInfo = () => {
    /* Giải thưởng lên ĐẦU tab: đây là thứ kéo người ta bấm đăng ký. Trừ khi chưa công
       bố - lúc đó khối này chỉ còn một câu từ chối, và mở tab ra mà câu đầu tiên là
       một lời từ chối thì thà để ngày tháng với địa điểm lên trước. */
    const coGiaiThuong = view.prizes.length > 0;
    const khoiGiaiThuong = (
      <>
        <div className="jhead">Giải thưởng</div>
        {renderTabPrize()}
      </>
    );

    return (
      <>
        {coGiaiThuong ? khoiGiaiThuong : null}
        <div className="sec">
          <div className="infoline">
            <div className="ic">📅</div>
            <div>
              <div className="k">Đăng ký</div>
              <div className="v acc">{league.reg}</div>
            </div>
          </div>
          <div className="divider" />
          <div className="infoline">
            <div className="ic">🎱</div>
            <div>
              <div className="k">Thi đấu</div>
              <div className="v">{league.play}</div>
            </div>
          </div>
          <div className="divider" />
          <div className="infoline">
            <div className="ic">📍</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="k">{selectedVenue ? "Địa điểm của bạn" : "Địa điểm thi đấu"}</div>
              {selectedVenue ? (
                <>
                  <div className="v">{selectedVenue.name}</div>
                  <div style={{ marginTop: 8 }}>
                    <span className="chip red">Đã cố định khi đăng ký</span>
                  </div>
                </>
              ) : view.venues.length > 0 ? (
                view.venues.map((v) => (
                  <div key={v.id} className="v" style={{ marginBottom: 6 }}>
                    {v.name}
                  </div>
                ))
              ) : (
                <div className="v">Giải chưa gán địa điểm</div>
              )}
            </div>
          </div>
        </div>
        {coGiaiThuong ? null : khoiGiaiThuong}
        <div className="jhead">Thể lệ thi đấu</div>
        {renderTabRule()}
      </>
    );
  };

  /* --------------------------------- Màn hình --------------------------------- */

  const renderScreenPre = () => (
    <>
      <Navbar brand={false} />
      <div className="pad">
        {banner ? (
          <div className="jbanner">
            <img alt={league.name} src={banner} />
            <span className="fmt chip solid">{league.format}</span>
          </div>
        ) : null}
        <h1 className="jname">{league.name}</h1>
        <div className="tabs">
          <button className={tab === "reg" ? "" : "on"} onClick={() => setTab("info")}>
            Thông tin chung
          </button>
          <button className={tab === "reg" ? "on" : ""} onClick={() => setTab("reg")}>
            Đã đăng ký
          </button>
        </div>
        <div className="tabpane">{tab === "reg" ? renderTabRegistered() : renderTabInfo()}</div>
        <div className="actionbar-spacer" />
      </div>
      <div className="actionbar">
        <div className="inner">
          {league.fee ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span className="tiny" style={{ marginLeft: "auto" }}>
                Lệ phí {money(league.fee)} đ
              </span>
            </div>
          ) : null}
          {/* Xem giải không cần đăng nhập, nhưng đăng ký thì có. */}
          {user?.id ? (
            <button
              className="btn btn-primary btn-block"
              disabled={busy || !canRegister}
              onClick={() => setModalOpen(true)}
            >
              {regCta}
            </button>
          ) : (
            <button className="btn btn-primary btn-block" onClick={() => navigate("/auth/login")}>
              Đăng nhập để tham gia
            </button>
          )}
        </div>
      </div>
    </>
  );

  const renderScreenJoined = () => {
    const active: Tab = tab === "play" || tab === "rank" ? tab : "info";

    return (
      <>
        <Navbar brand={false} />
        <div className="pad">
          {banner ? (
            <div className="jbanner">
              <img alt={league.name} src={banner} />
              <span className="fmt chip solid">{league.format}</span>
            </div>
          ) : null}
          <h1 className="jname">{league.name}</h1>
          <div className="tabs">
            <button className={active === "info" ? "on" : ""} onClick={() => setTab("info")}>
              Thông tin chung
            </button>
            <button className={active === "play" ? "on" : ""} onClick={() => setTab("play")}>
              Thi đấu
            </button>
            <button className={active === "rank" ? "on" : ""} onClick={() => setTab("rank")}>
              Sơ đồ & BXH
            </button>
          </div>
          <div className="tabpane">
            {active === "info" && renderTabInfo()}
            {active === "play" && renderTabPlay()}
            {active === "rank" && renderTabRank()}
          </div>
        </div>
      </>
    );
  };

  /* Bốn khối dưới đây là HÀM DỰNG JSX, không phải component — và gọi thẳng renderX()
     chứ không viết <X />.

     Lý do: định nghĩa một component bên trong component cha thì mỗi lần cha render lại
     sinh ra một KIỂU component mới, React coi là cây khác nên tháo ra dựng lại từ đầu.
     Sheet và modal có animation lúc xuất hiện, nên cứ mỗi lần bấm +/- ở ô nhập điểm là
     animation chạy lại — đúng cái nháy mà người dùng thấy.

     MỌI khối dựng JSX trong tệp này đều theo quy ước đó, không riêng sheet và modal.
     Các tab từng viết <TabRank /> và trả giá đắt hơn nhiều: trang tự nạp lại 20 giây
     một lần, mỗi lần nạp là cả tab bị tháo ra dựng lại, nên sơ đồ nhánh kép mất sạch
     vị trí kéo/phóng và giật về khung nhìn mặc định — người dùng báo đúng triệu chứng
     này. */

  const renderResultSheet = () => {
    const current = me.match;
    if (!current) {
      return null;
    }
    /* Chạm của TRẬN, không phải chạm của giải: vòng chung kết đặt được chạm riêng, nên
       lấy league.race thì ô nhập bị chặn ở chạm vòng loại — trận chung kết chạm 9 mà
       bấm mãi không quá 7, và tỉ số gửi lên bị backend từ chối vì nó xét match.race. */
    const race = current.race || league.race;
    const valid = sheetScore.me === race || sheetScore.opp === race;
    const step = (who: "me" | "opp", delta: number) =>
      setSheetScore((score) => ({
        ...score,
        [who]: Math.max(0, Math.min(race, score[who] + delta)),
      }));

    return (
      <div className="sheetwrap">
        <div className="sheetbd" onClick={() => setSheetOpen(false)} />
        <div className="sheet">
          <div className="grab" />
          <div className="sc">
            <div className="sttl">Nhập kết quả</div>
            <div className="small">
              {me.branch ?? "Sơ đồ"}
              {current.table ? ` • Bàn ${current.table}` : ""}
              {current.venueName ? ` • ${current.venueName}` : ""} • Chạm {race}
            </div>
            <div className="scoreinput">
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <div className="ava me">{initials(user?.name || "")}</div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Bạn</div>
              </div>
              <div className="stepper">
                <button onClick={() => step("me", -1)}>–</button>
                <span className="sv">{sheetScore.me}</span>
                <button onClick={() => step("me", 1)}>+</button>
              </div>
            </div>
            <div className="scoreinput">
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <Avatar
                  src={current.opponentAvatar}
                  name={current.opponentName || ""}
                  rankClass={current.opponentRankClass}
                  score={current.opponentRating}
                />
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {current.opponentName}
                </div>
              </div>
              <div className="stepper">
                <button onClick={() => step("opp", -1)}>–</button>
                <span className="sv">{sheetScore.opp}</span>
                <button onClick={() => step("opp", 1)}>+</button>
              </div>
            </div>
            <div className="tiny" style={{ marginTop: 8 }}>
              {valid ? (
                <>
                  Người thắng:{" "}
                  <b style={{ color: "var(--red)" }}>
                    {sheetScore.me > sheetScore.opp ? "Bạn" : current.opponentName}
                  </b>{" "}
                  • {(me.lives ?? 2) > 1 ? "người thua mất 1 mạng, xuống nhánh thua" : "người thua dừng bước"}.
                </>
              ) : (
                <span style={{ color: "var(--st-wait)" }}>Một bên phải đạt {race} ván để kết thúc trận.</span>
              )}
            </div>
            <div className="callout" style={{ marginTop: 14 }}>
              <div className="tiny" style={{ color: "#F0BDB4" }}>
                Kết quả chỉ được tính sau khi đối thủ xác nhận. Sau khi cả hai xác nhận, trận được tính vào
                rating Biadi và không sửa được.
              </div>
            </div>
            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 14 }}
              disabled={!valid || busy}
              onClick={handleSubmitResult}
            >
              Gửi kết quả
            </button>
            <button className="btn btn-quiet btn-block" style={{ marginTop: 8 }} onClick={() => setSheetOpen(false)}>
              Huỷ
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* Xác nhận là bước không quay lại được (trận vào rating, không sửa nữa), nên phải
     hỏi lại một nhịp và cho người ta nhìn rõ con số mình sắp đồng ý. */
  const renderConfirmResultSheet = () => {
    const current = me.match;
    const score = pendingScore(current);
    if (!current || !score) {
      return null;
    }
    const iWin = score.me > score.opp;

    return (
      <div className="sheetwrap">
        <div className="sheetbd" onClick={() => setConfirmSheetOpen(false)} />
        <div className="sheet">
          <div className="grab" />
          <div className="sc">
            <div className="sttl">Xác nhận kết quả</div>
            <div className="small">
              {me.branch ?? "Sơ đồ"}
              {current.table ? ` • Bàn ${current.table}` : ""}
              {current.venueName ? ` • ${current.venueName}` : ""} • Chạm {current.race || league.race}
            </div>
            <div className="pendingscore" style={{ marginTop: 12 }}>
              <span className="ps-side">Bạn</span>
              <span className="ps-val">
                {score.me}
                <span className="ps-sep">–</span>
                {score.opp}
              </span>
              <span className="ps-side ps-right">{current.opponentName || "Đối thủ"}</span>
            </div>
            <div className="tiny" style={{ marginTop: 8 }}>
              Người thắng: <b style={{ color: "var(--red)" }}>{iWin ? "Bạn" : current.opponentName}</b> •{" "}
              {(me.lives ?? 2) > 1 ? "người thua mất 1 mạng, xuống nhánh thua" : "người thua dừng bước"}.
            </div>
            <div className="callout" style={{ marginTop: 14 }}>
              <div className="tiny" style={{ color: "#F0BDB4" }}>
                Sau khi xác nhận, trận được tính vào rating Biadi và không sửa được. Nếu tỉ số chưa đúng, hãy
                đóng lại và chọn "Nhập tỉ số khác".
              </div>
            </div>
            <button
              className="btn btn-primary btn-block"
              style={{ marginTop: 14 }}
              disabled={busy}
              onClick={handleConfirmResult}
            >
              Xác nhận
            </button>
            <button
              className="btn btn-quiet btn-block"
              style={{ marginTop: 8 }}
              onClick={() => setConfirmSheetOpen(false)}
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmJoinModal = () => (
    <div className="modalwrap">
      <div className="modalbd" onClick={() => setModalOpen(false)} />
      <div className="modal">
        <div className="label" style={{ marginBottom: 10 }}>
          Xác nhận tham gia
        </div>
        <div className="callout">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{league.name}</div>
          <div className="small" style={{ color: "#E9CFC9" }}>
            Bạn đăng ký tham gia giải này — thể thức {league.format}, hai mạng, chạm {league.race}
            {league.fee
              ? league.feeQrImage || league.feeTransferNote
                ? `. Lệ phí ${money(league.fee)} đ — đăng ký xong bấm Đóng phí để lấy mã QR`
                : `. Lệ phí ${money(league.fee)} đ đóng tại chỗ khi check-in`
              : ""}
            .
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={handleJoin}>
            Xác nhận
          </button>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setModalOpen(false)}>
            Để sau
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="biadi">
      <div className="shell">{joined ? renderScreenJoined() : renderScreenPre()}</div>
      {sheetOpen && renderResultSheet()}
      {confirmSheetOpen && renderConfirmResultSheet()}
      {modalOpen && renderConfirmJoinModal()}
      {feeModalOpen && (
        <FeePaymentModal
          league={league}
          feeDeadline={league.feeDeadlineDate}
          phone={user?.phone}
          billUploaded={me.billUploaded}
          billImage={me.billImage}
          /* Nạp lại khi đóng: nộp biên lai xong mà không nạp thì lần mở sau ô này lại
             trông như chưa nộp gì - và người dùng nộp đi nộp lại. */
          onClose={() => {
            setFeeModalOpen(false);
            load();
          }}
        />
      )}
      <AvatarZoom anh={anhPhongTo} onClose={() => setAnhPhongTo(null)} />
    </div>
  );
}
